import { createContext, useEffect, useReducer } from 'react'
import { Logger, useSessionStorage, createAction } from '@wolfdanepug/common-react'
import reducer from './reducer'
import initialState from './state'
import { CartItem } from './types'
import {
	CLEAR_CART,
	SET_CHANNEL,
	SET_DISCOUNT_CODE,
	SET_REFERRAL,
	UPDATE_QUANTITY
} from './actions'
import useQueryStringParams from '../../lib/useQueryStringParams'
import { gql, useApolloClient } from '@apollo/client'
import { pick } from 'lodash'
import { ONBOARDING_COUPON } from '../../constants'

export const CartContext = createContext<any>({
	useCartState: () => ({}),
	addToCart: (item: CartItem) => {},
	updateQuantity: (id: string, quantity: number) => {},
	removeFromCart: (id: string, cb?: any) => {},
	clearCart: () => {},
	getCoupon: (code: string) => {}
})

const FindContent = gql`
	query FindContent($filter: JSON, $type: String) {
		findContent(filter: $filter, type: $type) {
			... on KeyRecordContext {
				id
				title
				description
				key
				context
				code
				contextId
				createdDate
				updatedDate
			}
		}
	}
`

export const CartProvider = ({ children }: any) => {
	const [state, dispatch] = useReducer(reducer, initialState)
	const apollo = useApolloClient()

	// Query String Params
	const params: any = useQueryStringParams()
	// Session Storage Discount Code
	const [discount, setDiscount] = useSessionStorage('discount-code', ONBOARDING_COUPON)
	const [campaign, setCampaign] = useSessionStorage('campaign', null)

	useEffect(() => {
		Logger.log('[Cart] state', state)
	}, [state])

	/**
	 * Controller
	 * @param {string} action
	 * @param {any} payload
	 * @returns {void}
	 */
	const controller = (action: string, payload?: any): void => {
		dispatch(createAction(action, payload))
	}

	/**
	 * Get the referral coupon code
	 * @param {string} key - Key Record Context ID
	 * @returns {Promise<void>}
	 */
	const getReferralCouponCode = async (key: string): Promise<void> => {
		try {
			const { data } = await apollo.query({
				query: FindContent,
				variables: {
					type: 'KeyRecordContext',
					filter: {
						contextId: key
					}
				}
			})

			Logger.log('[Cart] getReferralCouponCode', data)
			setDiscount(data.findContent.code)
		} catch (e: any) {
			Logger.error('[Cart] getReferralCouponCode', e)
		}
	}

	/**
	 * Get the key record context
	 * @param {string} key
	 * @returns {Promise<void>}
	 */
	const getKeyRecordContext = async (key: string): Promise<void> => {
		controller(SET_REFERRAL, key)
		;(discount === 'null' || discount === null) && (await getReferralCouponCode(key))
		Promise.resolve()
	}

	useEffect(() => {
		const picked = pick(params, ['c', 'k', 'm'])
		Object.keys(picked).length && setCampaign(picked)
	}, [params])

	// if key param is present, get the key record context
	useEffect(() => {
		params?.k && getKeyRecordContext(params.k)
	}, [params?.k])

	// if channel param is present, set the channel
	useEffect(() => {
		params?.c && controller(SET_CHANNEL, params.c)
	}, [params?.c])

	// if state discount code is not set and discount code is present, get the coupon
	useEffect(() => {
		if (!state.discountCode && discount) {
			controller(SET_DISCOUNT_CODE, discount)
		}
	}, [state?.discountCode, discount])

	// Use Cart State Hook
	const useCartState = () => {
		return state
	}

	return (
		<CartContext.Provider
			value={{
				useCartState,
				updateQuantity: (id: string, quantity: number) =>
					controller(UPDATE_QUANTITY, { id, quantity }),
				clearCart: () => controller(CLEAR_CART)
			}}
		>
			{children}
		</CartContext.Provider>
	)
}
